import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-4c365c0b"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "tooltip-text"
}
const _hoisted_2 = {
  key: 1,
  class: "tooltip-content"
}
const _hoisted_3 = ["innerHTML"]

import {
  ref,
  computed,
  watch,
  nextTick,
} from "vue";

interface Props {
  visible: boolean;
  content: string | object | null;
  x: number;
  y: number;
  offset?: { x: number; y: number };
  preventHover?: boolean; // For fretboard tooltips that shouldn't interfere with mouse events
  isAbove?: boolean; // For smart positioning above/below notes
  isLeftSide?: boolean; // For left/right positioning (true = left side, false = right side)
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TooltipContainer',
  props: {
    visible: { type: Boolean },
    content: {},
    x: {},
    y: {},
    offset: { default: () => ({ x: 8, y: 8 }) },
    preventHover: { type: Boolean, default: false },
    isAbove: { type: Boolean, default: false },
    isLeftSide: { type: Boolean, default: undefined }
  },
  emits: ["mouseenter", "mouseleave"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const tooltipEl = ref<HTMLElement | null>(null);

const tooltipStyle = computed(() => {
  const style: Record<string, string> = {
    position: "fixed",
    left: `${props.x + props.offset.x}px`,
    top: `${props.y + props.offset.y}px`,
    zIndex: "10000",
  };

  // Handle different positioning modes
  if (props.isLeftSide !== undefined) {
    // Left/right positioning for fretboard tooltips
    if (props.isLeftSide) {
      // Position on left side - tooltip right edge aligns with anchor point
      style.transform = "translateX(-100%) translateY(-50%)";
    } else {
      // Position on right side - tooltip left edge starts at anchor point
      style.transform = "translateY(-50%)";
    }
  } else {
    // Default centered positioning for above/below tooltips
    style.transform = "translateX(-50%)";
  }

  if (props.preventHover) {
    style.pointerEvents = "none";
  }

  return style;
});

// Handle mouse events for non-fretboard tooltips
function handleMouseEnter(event: MouseEvent) {
  if (!props.preventHover) {
    emit("mouseenter", event);
  }
}

function handleMouseLeave(event: MouseEvent) {
  if (!props.preventHover) {
    emit("mouseleave", event);
  }
}

// Auto-adjust position to stay within viewport and handle overflow
watch(
  [() => props.visible, () => props.x, () => props.y, () => props.isAbove, () => props.isLeftSide],
  async () => {
    if (props.visible) {
      await nextTick();

      if (tooltipEl.value) {
        const rect = tooltipEl.value.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Handle isAbove positioning by adjusting based on actual tooltip height
        if (props.isAbove) {
          // Position tooltip above the anchor point, accounting for its full height
          const adjustedY = props.y - rect.height + props.offset.y;
          tooltipEl.value.style.top = `${adjustedY}px`;
        }

        // Handle horizontal overflow for left/right positioning
        if (props.isLeftSide !== undefined) {
          // For fretboard tooltips with left/right positioning
          if (rect.right > viewportWidth && !props.isLeftSide) {
            // Right-side tooltip overflowing: switch to left-side positioning
            // Need to adjust the x position since original was calculated for right-side
            const noteRadius = 24;
            const horizontalOffset = 8;
            const leftSideX = props.x - (noteRadius + horizontalOffset) * 2 + props.offset.x;
            tooltipEl.value.style.left = `${leftSideX}px`;
            tooltipEl.value.style.transform = "translateX(-100%) translateY(-50%)";
          } else if (rect.left < 0 && props.isLeftSide) {
            // Left-side tooltip overflowing: switch to right-side positioning
            // Need to adjust the x position since original was calculated for left-side
            const noteRadius = 24;
            const horizontalOffset = 8;
            const rightSideX = props.x + (noteRadius + horizontalOffset) * 2 + props.offset.x;
            tooltipEl.value.style.left = `${rightSideX}px`;
            tooltipEl.value.style.transform = "translateY(-50%)";
          }
        } else {
          // Handle horizontal overflow for above/below positioning (legacy)
          if (rect.right > viewportWidth) {
            tooltipEl.value.style.left = `${
              props.x - rect.width - props.offset.x
            }px`;
          }
        }

        // Adjust vertical position if tooltip would go off-screen
        if (rect.bottom > viewportHeight && !props.isAbove) {
          tooltipEl.value.style.top = `${
            props.y - rect.height - props.offset.y
          }px`;
        }

        // For above positioning, adjust if it goes above viewport
        if (props.isAbove && rect.top < 0) {
          tooltipEl.value.style.top = `${props.y + props.offset.y}px`;
        }
      }
    }
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "tooltip" }, {
      default: _withCtx(() => [
        (_ctx.visible)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "tooltipEl",
              ref: tooltipEl,
              class: "tooltip-container",
              style: _normalizeStyle(tooltipStyle.value),
              onMouseenter: handleMouseEnter,
              onMouseleave: handleMouseLeave
            }, [
              (typeof _ctx.content === 'string')
                ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(_ctx.content), 1))
                : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _renderSlot(_ctx.$slots, "content", { data: _ctx.content }, () => [
                      (typeof _ctx.content === 'string')
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            innerHTML: _ctx.content
                          }, null, 8, _hoisted_3))
                        : _createCommentVNode("", true)
                    ])
                  ]))
            ], 36))
          : _createCommentVNode("", true)
      ]),
      _: 3
    })
  ]))
}
}

})